/** A toast whose undo is a real control, and absent when there is nothing to undo.
 *
 *  `feedback/Toast.jsx` has two defects. Its action is a `<span onClick>` — the
 *  same missing semantics as CaptureBar, TabBar, SheetAction and SegmentedTabs,
 *  and this one matters more than most: the undo is the thing that makes a
 *  one-tap Complete safe, and a screen-reader user could not reach it at all.
 *  And `actionLabel` defaults to `'UNDO'` while the render guard is
 *  `onAction || actionLabel`, so every toast in the app has been painting a dead
 *  UNDO with no handler behind it.
 *
 *  Same visual spec — one ellipsized line, an amber label that never truncates,
 *  a heavy-bordered sheet ground. Reported upstream; delete this when Toast
 *  takes a real button and stops defaulting its label.
 */
export function UndoToast({ text, onUndo }: { text: string; onUndo?: () => void }) {
  return (
    <div
      role="status"
      style={{
        display: 'flex',
        gap: 'var(--bujo-space-4)',
        alignItems: 'center',
        background: 'var(--bujo-surface-sheet)',
        border: 'var(--bujo-border-heavy)',
        padding: '12px 14px',
      }}
    >
      <span
        style={{
          flex: 1,
          minWidth: 0,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          font: 'var(--bujo-fw-regular) 12.5px/var(--bujo-lh-row) var(--bujo-font-mono)',
          color: 'var(--bujo-text-primary)',
        }}
      >
        {text}
      </span>
      {onUndo ? (
        <button
          type="button"
          onClick={onUndo}
          style={{
            flex: 'none',
            // Full row height so the target is reachable, without changing the
            // toast's own height — the label sits in the middle of it.
            minHeight: 'var(--bujo-row-min)',
            margin: '-12px 0',
            padding: '0 var(--bujo-space-2)',
            background: 'none',
            border: 0,
            cursor: 'pointer',
            font: 'var(--bujo-fw-bold) var(--bujo-fs-label)/var(--bujo-lh-tight) var(--bujo-font-mono)',
            letterSpacing: 'var(--bujo-ls-badge)',
            color: 'var(--bujo-accent)',
          }}
        >
          UNDO
        </button>
      ) : null}
    </div>
  );
}
